import React, { useState, useCallback, useEffect } from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import yup from '../../../utility/yup';
import { Project, DuplicateProject } from '../../../types/items';
import {
  Action,
  ReactHookFormModal,
} from '../../../components';
import { CopyIcon } from '../../../components/icon/regular';
import { useActionDispatcher } from '../../../hooks/index';
import {
  duplicateProjectRequest,
  initProjectRequest,
} from '../../../store/modules/projects/actions';
import DuplicateForm from '../tab-overview/DuplicateForm';
import hotKeyConverter from '../../../utility/keyCodeConverter';
import { selectHoveredItemId } from '../../../store/modules/lists/selectors';
import { selectCurrentUser } from '../../../store/modules/users/selectors';
import {selectDuplicateProject} from "../../../store/modules/projects/selectors";

type Props = {
  original: Project;
};

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  icon: {
    color: palette.icons.default,
    fontSize: '1rem',
  },
}));

const schema = yup.object().shape({
  title: yup.string().required().trim().label('Title'),
  user_owner_id: yup.number().min(1, 'Owner is required').label('Owner'),
});

const DuplicateAction = ({ original }: Props) => {
  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch();
  const { data: user } = useSelector(selectCurrentUser);
  const hoveredItemId = useSelector(selectHoveredItemId);
  const { loading } = useSelector(selectDuplicateProject);

  const duplicateProjectForm: DuplicateProject = {
    title: `${original.title} - copy`,
    is_template: false,
    copy_team: false,
    copy_scene_attachments: true,
    copy_scripts: true,
    user_owner_id: user?.id || -1,
    organization_id: -1,
    project_id: original.id,
  };

  const onSubmitHandler = useActionDispatcher(duplicateProjectRequest);

  const openModalHandler = useCallback(() => {
    if (!openModal) {
      dispatch(initProjectRequest());
      setOpenModal(true);
    }
  }, [setOpenModal, dispatch, openModal]);

  const closeModalHandler = useCallback(() => {
    setOpenModal(false);
  }, [setOpenModal]);

  useEffect(() => {
    const hotKeyHandler = (event: KeyboardEvent) => {
      if (!event.altKey && !event.ctrlKey && event.keyCode === hotKeyConverter('d')) {
        if (hoveredItemId === original.id) {
          event.preventDefault();
          openModalHandler();
        }
      }
    };
    window.addEventListener('keydown', hotKeyHandler);
    return () => {
      window.removeEventListener('keydown', hotKeyHandler);
    };
  }, [openModalHandler, hoveredItemId, original]);

  return (
    <>
      <Action
        title="Duplicate"
        icon={<CopyIcon className={classes.icon} />}
        onClick={openModalHandler}
      />
      {openModal && (
        <ReactHookFormModal
          formSettings={{
            initialValues: duplicateProjectForm,
            validationSchema: schema,
            onSubmit: onSubmitHandler,
            submitTitle: 'Duplicate',
            loading,
          }}
          open={openModal}
          onCloseHandler={closeModalHandler}
          title="Duplicate Project"
        >
          <DuplicateForm clone={original} />
        </ReactHookFormModal>
      )}
    </>
  );
};

export default DuplicateAction;
